import React from 'react';
import styled, { css } from 'styled-components';

import { Product } from 'types/graphql-overrides';
import { Link } from 'components/core';
import Imgix from 'shared/components/imgix';
import { ImgixDefaultSettings } from 'shared/constants';
import { imageToUse } from 'shared/helpers/products';
import usePaths from 'hooks/use-paths';
import { useProductCardData } from 'src/components/product-card-v2/use-product-card-data';
import PlusSymbol from 'src/assets/plus-symbol';

import { BuyItAgainCarouselItem, BuyItAgainOutOfStockItem } from './use-buy-it-again-carousel';
import { StrainBadge, PotencyText, overlayButtonStyles } from './buy-it-again-shared-styles';

type BuyItAgainSimpleTileProps = {
  item: BuyItAgainCarouselItem;
  index: number;
  onProductClick: (product: Product, index: number) => void;
  onQuickAddClick: (product: Product, index: number) => void;
  onOutOfStockClick: (item: BuyItAgainOutOfStockItem) => void;
};

export function BuyItAgainSimpleTile({
  item,
  index,
  onProductClick,
  onQuickAddClick,
  onOutOfStockClick,
}: BuyItAgainSimpleTileProps): JSX.Element {
  if (item.type === 'available') {
    return (
      <AvailableTile product={item.product} index={index} onClick={onProductClick} onQuickAddClick={onQuickAddClick} />
    );
  }

  return <OutOfStockTile item={item} onClick={onOutOfStockClick} />;
}

type AvailableTileProps = {
  product: Product;
  index: number;
  onClick: (product: Product, index: number) => void;
  onQuickAddClick: (product: Product, index: number) => void;
};

/**
 * The quick-add button lives inside the tile alongside the full-tile link, so we can't
 * simply wrap the whole tile in an anchor (that would nest a <button> inside an <a>).
 * Instead, an absolutely-positioned anchor overlay covers the tile and the button sits on
 * top of it (later in DOM order, so it wins pointer events in its own area) — the same
 * pattern used by product-card-v2's CardLink + CardActionButton.
 */
function AvailableTile({ product, index, onClick, onQuickAddClick }: AvailableTileProps): JSX.Element {
  const data = useProductCardData({ product, productIndex: index });
  const { strainType, potency } = data.product;
  const {
    displayPrice,
    standardPrice,
    discount,
    listPriceEnabled,
    hideStrikethrough,
    formatStandardPrice,
  } = data.pricing;

  const handleQuickAddClick = (event: React.MouseEvent): void => {
    event.preventDefault();
    event.stopPropagation();
    onQuickAddClick(product, index);
  };

  return (
    <TileShell data-testid='buy-it-again-simple-tile'>
      <Link href={data.product.href} onClick={() => onClick(product, index)} aria-label={data.product.name}>
        <TileAnchor />
      </Link>

      <Info>
        <Name>{data.product.name}</Name>
        {data.product.brandName && <BrandName>{data.product.brandName}</BrandName>}

        {(Boolean(strainType) || potency.length > 0) && (
          <StrainDetails>
            {strainType && <StrainBadge>{strainType}</StrainBadge>}
            {potency.map((line) => (
              <PotencyText key={line}>{line}</PotencyText>
            ))}
          </StrainDetails>
        )}

        <PriceRow>
          <ActivePrice>{displayPrice}</ActivePrice>

          {discount && (
            <>
              <StandardPrice $hideStrikethrough={hideStrikethrough}>
                {standardPrice && listPriceEnabled ? formatStandardPrice(standardPrice) : standardPrice}
              </StandardPrice>
              {!hideStrikethrough && <DiscountTag $listPriceEnabled={listPriceEnabled}>{discount}</DiscountTag>}
            </>
          )}
        </PriceRow>
      </Info>

      <ImageSection>
        <Imgix
          imgixParams={{ ...ImgixDefaultSettings, fit: 'cover', ...data.image.imgixAttrs }}
          width={198}
          height={198}
          htmlAttributes={{ alt: data.product.name, loading: 'lazy' }}
          src={data.image.fallbackImage}
        />
        <AddButton
          onClick={handleQuickAddClick}
          aria-label={`Add ${data.product.name} to cart`}
          data-testid='buy-it-again-tile-quick-add'
        >
          <PlusSymbol fill='var(--dcm-filter-text-color, #121516)' />
        </AddButton>
      </ImageSection>
    </TileShell>
  );
}

type OutOfStockTileProps = {
  item: BuyItAgainOutOfStockItem;
  onClick: (item: BuyItAgainOutOfStockItem) => void;
};

/**
 * Unlike AvailableTile, the card itself navigates to the item's own product page (which has
 * its own "notify me when back in stock" affordance — see src/dispensary/product/product.jsx)
 * rather than opening the substitute modal. "View similar" is the dedicated action for the
 * modal, matching the pattern of a visible button layered over an invisible full-card link.
 */
function OutOfStockTile({ item, onClick }: OutOfStockTileProps): JSX.Element {
  const { originalProduct } = item;
  // originalProduct.Image is frequently unset (no photo was ever uploaded for the product) —
  // fall back to the same brand/category stock image used by in-stock cards instead of a blank box.
  const imageUrl: string | null = imageToUse(originalProduct) ?? null;
  const name = originalProduct.Name ?? '';
  const brandName = originalProduct.brandName ?? '';
  const { href: productHref } = usePaths({ product: originalProduct });

  const handleViewSimilarClick = (event: React.MouseEvent): void => {
    event.stopPropagation();
    onClick(item);
  };

  return (
    <TileShellStatic data-testid='buy-it-again-simple-tile-oos'>
      <Link href={productHref} aria-label={name || 'Item'}>
        <TileAnchor />
      </Link>

      <Info>
        <FadedContent>
          <Name>{name}</Name>
          {brandName && <BrandName>{brandName}</BrandName>}
        </FadedContent>
        <OutOfStockLabel>Out of stock</OutOfStockLabel>
      </Info>

      <ImageSection>
        <FadedImageWrapper>
          {imageUrl ? (
            <Imgix
              imgixParams={{ ...ImgixDefaultSettings, fit: 'cover' }}
              width={198}
              height={198}
              htmlAttributes={{ alt: name, loading: 'lazy' }}
              src={imageUrl}
            />
          ) : (
            <ImagePlaceholder />
          )}
        </FadedImageWrapper>
        <ViewSimilarButton onClick={handleViewSimilarClick} data-testid='buy-it-again-tile-view-similar'>
          View similar
        </ViewSimilarButton>
      </ImageSection>
    </TileShellStatic>
  );
}

// Matches the standard product card's hover treatment (product-card-v2/card.tsx's Wrapper)
// so these carousel cards feel consistent with every other card in the app's carousels.
const tileShellStyles = css`
  background: var(--dcm-color-body-bg, #fff);
  border: 1px solid var(--dcm-buy-it-again-group-border-color, #e3e7e9);
  border-radius: ${({ theme }) => theme.customized.radius.lg};
  display: flex;
  flex: 0 0 auto;
  height: 152px;
  overflow: hidden;
  position: relative;
  transition: all 0.2s;
  width: 300px;

  @media screen and (min-width: 1110px) {
    height: 198px;
    width: 380px;
  }

  &:hover {
    border-color: var(--dcm-buy-it-again-tile-hover-border, #d2d7da);
    box-shadow: var(--dcm-buy-it-again-tile-hover-shadow, 0 4px 10px 0 #0000000d);
  }
`;

const TileShell = styled.div`
  ${tileShellStyles}
`;

const TileShellStatic = styled.div`
  ${tileShellStyles}
`;

// Fades the product presentation (name/brand/image) without dimming the "Out of stock" badge,
// "View similar" button, or the invisible full-card overlay button that opens the modal —
// nested opacity can't un-fade a child, so this has to sit below those elements, not above.
const FadedContent = styled.div`
  opacity: 0.5;
`;

const FadedImageWrapper = styled.div`
  height: 100%;
  opacity: 0.5;
  width: 100%;
`;

// A plain div, not an anchor — Link (components/core/link) renders the real <a> itself
// when its child isn't already an anchor element. Matches the CardLink/AnchorWrapper
// pattern in product-card-v2/card.tsx.
const TileAnchor = styled.div`
  bottom: 0;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 1;
`;

const Info = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  padding: 14px;
`;

const Name = styled.p`
  color: var(--dcm-filter-text-color, #121516);
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.25;
  overflow: hidden;

  /* ECOMM-13707: without this, flexbox can shrink Name below its 2-line-clamp height when
     siblings need room, clipping the title without an ellipsis (cut off or blank). */
  flex-shrink: 0;
  min-height: calc(1.25em * 2);
`;

const BrandName = styled.p`
  color: var(--dcm-buy-it-again-tile-brand-color, #646d72);
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  /* ECOMM-13707: same as Name -- without this, BrandName becomes the next row flexbox
     shrinks to compensate, cropping it into garbled/overlapping text. */
  flex-shrink: 0;
`;

const StrainDetails = styled.div`
  align-items: center;
  display: flex;
  gap: 6px;

  /* ECOMM-13707: was flex-wrap: wrap -- multiple badges/chips (e.g. THC + CBD) could wrap to
     a 2nd line and blow the tile's height budget. Keep it one row; excess just clips. */
  flex-shrink: 0;
  flex-wrap: nowrap;
  overflow: hidden;
`;

const PriceRow = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: auto;
`;

const ActivePrice = styled.p`
  color: var(--dcm-filter-text-color, #121516);
  font-size: 16px;
  font-weight: 700;
`;

const StandardPrice = styled.span<{ $hideStrikethrough?: boolean }>`
  color: var(--dcm-buy-it-again-tile-brand-color, #646d72);
  font-size: 12px;
  text-decoration: ${({ $hideStrikethrough }) => ($hideStrikethrough ? 'none' : 'line-through')};
`;

const DiscountTag = styled.span<{ $listPriceEnabled?: boolean }>`
  background: ${({ $listPriceEnabled, theme }) =>
    $listPriceEnabled ? 'var(--dcm-product-discount-color, #d34a4a)' : theme.customized.colors.discountTag};
  border-radius: ${({ $listPriceEnabled, theme }) => ($listPriceEnabled ? '4px' : theme.customized.radius.tags)};
  color: var(--dcm-buy-it-again-discount-tag-color, #fff);
  font-size: 11px;
  font-weight: 600;
  padding: 2px 6px;
  white-space: nowrap;
`;

const OutOfStockLabel = styled.p`
  align-self: flex-start;
  background: var(--dcm-buy-it-again-oos-badge-bg, rgba(0, 0, 0, 0.75));
  border-radius: 4px;
  color: var(--dcm-buy-it-again-oos-badge-color, #fff);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  margin-top: auto;
  padding: 4px 6px;
  text-transform: uppercase;
`;

const ImageSection = styled.div`
  flex: 0 0 auto;
  height: 100%;
  position: relative;
  width: 152px;

  @media screen and (min-width: 1110px) {
    width: 198px;
  }

  /* Imgix renders a real <img> sized to the pixel width/height passed to it (198x198,
     matching the desktop tile) — without this it doesn't shrink to fit the 152px mobile
     tile and gets clipped by the tile's overflow: hidden instead of scaling down. */
  img {
    display: block;
    height: 100%;
    object-fit: cover;
    width: 100%;
  }
`;

const ImagePlaceholder = styled.div`
  background-color: ${({ theme }) => theme.colors.grey[90]};
  height: 100%;
  width: 100%;
`;

const AddButton = styled.button`
  ${overlayButtonStyles}
  border-radius: 50%;
  bottom: 12px;
  height: 36px;
  position: absolute;
  right: 12px;
  width: 36px;
  z-index: 2;
`;

const ViewSimilarButton = styled.button`
  ${overlayButtonStyles}
  border-radius: 9999px;
  bottom: 12px;
  color: var(--dcm-filter-text-color, #121516);
  font-size: 11px;
  font-weight: 700;
  padding: 8px 10px;
  position: absolute;
  right: 12px;
  z-index: 2;
`;
